import { Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import {ɵgetDOM as getDOM} from '@angular/common';

/**
 * Custom Event plugin as shown at https://medium.com/javascript-everyday/two-fancy-ways-to-handle-events-in-angular-397f6a45f893
 */
@Injectable({
    providedIn: 'root'
})
export class ClickEventHandlerService {
    clickEvent$: Subject<Event>;
    mousedownEvent$: Subject<Event>;

    constructor() {
        this.clickEvent$ = new Subject();
        this.mousedownEvent$ = new Subject();
    }

    /**
     * Defines the type of event to handle
     */
    supports(event: string) {
        if (event === 'click') {
            return true;
        }

        if (event === 'mousedown') {
            return true;
        }

        return event === 'click';
    }

    /**
     * Adds custom handler to click events on provided element
     *
     * Useful for adding logic to every click event, for example if you needed to stop event propogation
     * in a common way for a group event handlers. See link in class comment
     */
    addEventListener(el: HTMLElement, eventName: string, originalHandler: Function) {
        const [nativeEventName] = eventName.split('.');
        const enhancedHandler = (event: Event) => {
            originalHandler(event);

            if (event.type === 'click') {
                this.clickEvent$.next(event);
            }

            if (event.type === 'mousedown') {
                this.mousedownEvent$.next(event);
            }

        };

        if (el.addEventListener && typeof el.addEventListener === 'function') {
            el.addEventListener(nativeEventName, enhancedHandler);

            return () => el.removeEventListener(nativeEventName, enhancedHandler);
        }

        return () => {};
    }

    addGlobalEventListener(target: string, eventName: string, originalHandler: Function) {
        let htmlElement;

        if (target === 'document') {
            htmlElement = document;
        }

        if (target === 'window') {
            htmlElement = window;
        }

        if (target === 'body') {
            htmlElement = document.body;
        }

        if (!htmlElement) {
            throw new Error(`Unsupported event target ${target} for event ${eventName}`);
        }

        htmlElement.addEventListener(eventName, originalHandler);

        return () => htmlElement.removeEventListener(eventName, originalHandler);
    }

    /**
     * Returns provider for click events
     */
    getClickListener(): Observable<Event> {
        return this.clickEvent$;
    }

    /**
     * Returns provider for mousedown events
     */
    getMousedownListener(): Observable<Event> {
        return this.mousedownEvent$;
    }
}
